import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { LockAuditLog, SidepanelLogsListTypes } from './types';
import useQueryState from '../table/hooks/use-query-state';
import usePrevious from '../../hooks/use-previous';
import useGetList from '../../hooks/use-get-list';
import useFilters from '../table/hooks/use-filters';
import formatLogsList from './helpers/format-logs-list';
import useScroll from '../../hooks/use-scroll';
import { actions } from './variables/lists';
import { PermissionEnum } from '../../base-types';
import { Filters, SchemaFilterType } from '../table/types';
import IconLoader from '../icons/loader';
import Table from '../table/table';

import cn from './styles.module.css';

export default function SidepanelLogsList({
  auditLogs,
  auditLogsPagination,
  panelIsOpen,
  activePermissions,
  userId,
  getList,
  schema,
  filtersSchema,
  isFetching,
  isLoading,
  columns,
  columnsLabels,
  listClassName,
  withoutChangeSearchParams,
}: SidepanelLogsListTypes) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    withoutChangeSearchParams,
  );

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<LockAuditLog>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
      withoutChangeSearchParams,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatLogsList(auditLogs as LockAuditLog[]);

    return data;
  }, [auditLogs, sort, filters]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    setValues('action', actions);
  }, []);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !auditLogs &&
      userId &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      panelIsOpen
    ) {
      const nextFilters: Filters = {
        ...filters,
        userId: {
          value: [userId],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: nextFilters,
      });
    }
  }, [
    filters,
    prevFilters,
    auditLogs,
    userId,
    search,
    activePermissions,
    panelIsOpen,
  ]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.locksLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={columnsLabels}
        onFilterChange={(filterValues) => {
          const nextFilters: Filters = {
            ...filterValues,
            userId: {
              value: [userId as string],
              type: SchemaFilterType.STRING,
            },
          };

          onFilter({
            ...nextFilters,
          });
        }}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isLoading || isFetching}
        take="20"
        setPage={(data) => {
          const filterValues: Filters = {
            ...filters,
            userId: {
              value: [userId as string],
              type: SchemaFilterType.STRING,
            },
          };

          setPage({ ...data, filters: filterValues });
        }}
        pagination={auditLogsPagination}
        error={errorList}
        withScroll
      />
    );
  }

  return (
    <div
      className={`${cn.securityLocksContent} ${listClassName}`}
      ref={scrollRef}
    >
      {content}
    </div>
  );
}
